function scaleValuesToMaxCount(values, maxCount) {
  const safeMaxCount = Math.max(1, Number(maxCount) || 1);

  const finite = values.filter((v) => Number.isFinite(v));
  const originalMax = finite.length ? Math.max(...finite) : 0;

  if (!originalMax) return values.map(() => 0);

  return values.map((value) => {
    const v = Number.isFinite(value) ? value : 0;
    return (v / originalMax) * safeMaxCount;
  });
}

const steppedByHalfZoom = (minZoom, maxZoom, minValue, maxValue) => {
  const expression = ['step', ['zoom'], minValue];
  const range = maxZoom - minZoom;
  if (!Number.isFinite(range) || range <= 0) return expression;

  const stepSize = 0.5;
  for (let zoom = minZoom; zoom <= maxZoom + 1e-9; zoom += stepSize) {
    const z = Math.min(maxZoom, Number(zoom.toFixed(6)));
    const t = (z - minZoom) / range;
    const value = minValue + (maxValue - minValue) * t;
    expression.push(Number(z.toFixed(1)), Number(value.toFixed(3)));
  }

  const lastStop = expression[expression.length - 2];
  if (!Number.isFinite(lastStop) || Math.abs(lastStop - maxZoom) > 1e-6) {
    expression.push(maxZoom, maxValue);
  }

  return expression;
};

const seededUnit = (seed) => {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

const hslToHex = (h, s, l) => {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const light = Math.max(0, Math.min(100, l)) / 100;

  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = light - c / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (hue < 60) {
    rPrime = c;
    gPrime = x;
  } else if (hue < 120) {
    rPrime = x;
    gPrime = c;
  } else if (hue < 180) {
    gPrime = c;
    bPrime = x;
  } else if (hue < 240) {
    gPrime = x;
    bPrime = c;
  } else if (hue < 300) {
    rPrime = x;
    bPrime = c;
  } else {
    rPrime = c;
    bPrime = x;
  }

  const toHex = (value) =>
    Math.round((value + m) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(rPrime)}${toHex(gPrime)}${toHex(bPrime)}`;
};

export const districtNumberToColor = (districtNumber) => {
  const district = Number.parseInt(districtNumber, 10);
  if (!Number.isFinite(district) || district <= 0) return '#d1d5db';

  const hue = (district * 137.508) % 360;
  const saturation = 88 + Math.round(seededUnit(district + 11) * 8); // 88-96
  const lightness = 72 + Math.round(seededUnit(district + 37) * 8); // 72-80

  return hslToHex(hue, saturation, lightness);
};

const AB604_DISTRICT_COUNT = 52;

export const ab604FillColorExpression = [
  'match',
  ['to-number', ['get', 'DISTRICT']],
  ...Array.from({ length: AB604_DISTRICT_COUNT }, (_, index) => {
    const district = index + 1;
    return [district, districtNumberToColor(district)];
  }).flat(),
  '#d1d5db'
];

export const ab604FillLayer = {
  id: 'ab604-fill',
  type: 'fill',
  minzoom: 8,
  paint: {
    'fill-color': ab604FillColorExpression,
    'fill-opacity': 0.075
  }
};

export const currentLocationLayer = {
  id: 'current-location-point',
  type: 'circle',
  minzoom: 10,
  paint: {
    'circle-radius': 10,
    'circle-color': 'rgba(0, 0, 0, .95)',
    'circle-stroke-width': 2,
    'circle-stroke-color': 'rgba(255, 255, 255, 0.95)'
  }
};

export const currentLocationLabel = {
  id: 'current-location-label',
  type: 'symbol',
  minzoom: 10,
  layout: {
    'text-field': 'You',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 14,
    'text-anchor': 'center',
    'text-allow-overlap': true
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.95)',
    'text-halo-color': 'rgba(0, 0, 0, .95)',
    'text-halo-width': 3
  }
};

export const clusterCountLayer = (maxCount) => {
  const safeMaxCount = Math.max(1, Number(maxCount) || 1);

  const minText = 13;
  const maxText = 22;

  return {
    id: 'cluster-count',
    type: 'symbol',
    source: 'postPoints',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-allow-overlap': true,
      'text-anchor': 'center',

      'text-size': [
        'let',
        't',
        ['min', 1, ['/', ['to-number', ['get', 'point_count']], safeMaxCount]],
        [
          '+',
          minText,
          [
            '*',
            (maxText - minText),
            ['*', ['var', 't'], ['var', 't']]
          ]
        ]
      ]
    },
    paint: {
      'text-color': '#000000',
      'text-halo-color': 'rgba(255, 255, 255, 0.95)',
      'text-halo-width': 1.5
    }
  };
};


export const clusterLayer = (maxCount) => {
  const safeMaxCount = Math.max(1, Number(maxCount) || 1);

  // Slightly larger + softer circles for better legibility.
  const minR = 11;
  const maxR = 31;

  // Quadratic scaling in [0..1] space:
  // t = clamp(point_count / maxCount, 0, 1)
  // radius = minR + (maxR - minR) * t^2
  return {
    id: 'clusters',
    type: 'circle',
    source: 'postPoints',
    filter: ['has', 'point_count'],
    paint: {
      'circle-radius': [
        'let',
        't',
        ['min', 1, ['/', ['to-number', ['get', 'point_count']], safeMaxCount]],
        [
          '+',
          minR,
          [
            '*',
            (maxR - minR),
            ['*', ['var', 't'], ['var', 't']]
          ]
        ]
      ],

      'circle-color': [
        'let',
        't',
        ['min', 1, ['/', ['to-number', ['get', 'point_count']], safeMaxCount]],
        [
          'interpolate',
          ['exponential', 2],
          ['var', 't'],
          0.0, '#2b6cb0',
          0.6, '#38a169',
          0.85, '#ecc94b',
          1.0, '#e53e3e'
        ]
      ],

      'circle-opacity': 0.5,
      'circle-stroke-width': 3,
      'circle-stroke-color': 'rgba(0, 0, 0, 0.9)',
      'circle-stroke-opacity': 0.9
    }
  };
};
export const unclusteredPointLayer = {
  id: 'unclustered-point',
  type: 'circle',
  source: 'postPoints',
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-radius': steppedByHalfZoom(5, 13, 6.2, 11),
    'circle-color': ['coalesce', ['get', 'iconColor'], '#000000'],
    'circle-opacity': 0.75,
    'circle-stroke-width': steppedByHalfZoom(5, 13, 1.6, 2.6),
    'circle-stroke-color': 'rgba(0, 0, 0, 1)',
    'circle-stroke-opacity': 0.9,
    'circle-blur': 0.02
  }
};

export const unclusteredPointTwinkleLayer = {
  id: 'unclustered-point-twinkle',
  type: 'circle',
  source: 'postPoints',
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-radius': steppedByHalfZoom(5, 13, 9.3, 16),
    'circle-color': ['coalesce', ['get', 'iconColor'], '#fde68a'],
    'circle-opacity': 0.14,
    'circle-blur': 0.82
  }
};

export const pointLayer = {
  id: 'projectCivilian',
  type: 'circle',
  paint: {
    'circle-color': ['coalesce', ['get', 'iconColor'], '#000000'],
    'circle-radius': 10,
    'circle-opacity': 0.75,
    'circle-stroke-width': 20,
    'circle-stroke-color': 'rgba(154, 154, 154, 1)',
    'circle-stroke-opacity': 0.09
  }
};

export const pointLayerLabel = {
  id: 'protest-location-label',
  type: 'symbol',
  source: 'projectCivilian',
  layout: {
    'text-field': 'Report',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 14,
    'text-anchor': 'center',
    'text-allow-overlap': true
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#ffffff',
    'text-halo-width': 1
  }
};
